import React, { useContext, useEffect, Fragment, useState } from "react";
import {clearProperties, fetchPagedProperties} from "../../../redux/actions/propertyActions";
import styled from "styled-components/macro";
import * as types from "../../../constants";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Box as MuiBox,
  Divider as MuiDivider,
  Typography as MuiTypography,
  makeStyles,
  Theme,
  useMediaQuery,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { Pagination } from "@material-ui/lab";
import { clearA11yes } from "../../../redux/actions/a11yesActions";
import {
  fetchProperty as propertyFetchProperty,
} from "../../../services/propertyService";
import { AppDispatchType, useSelector } from "../../../redux/store";
import { QuestionnaireContext } from "../../../components/contexts/QuestionnaireContext";
import { urls, useRouting } from "../../../routes";
import { useDispatch } from "react-redux";
import { clearProperty } from "../../../redux/actions-declarations/property";
import GridSkeleton from "../../../components/GridSkeleton";
import CreateNewProperty from "./components/CreateNewProperty";
import PropertyFilters from "./components/PropertyFilters";
import PropertySearch from "./components/PropertySearch";
import RenderProperties from "./components/RenderProperties";
import {fetchCompaniesInLive} from "../../../redux/actions/companyActions";
import {IUser} from "../../../models/User";
import {getUserProfile} from "../../../selectors";
import {fetchUser} from "../../../redux/actions/userActions";
import Typography from "@mui/material/Typography";

const Box = styled(MuiBox)(spacing);
const Divider = styled(MuiDivider)(spacing);

const useStyles = makeStyles((theme: Theme) => ({
  box: {
    // height: "calc(100% + 50px)",
  }
}))

const SearchWrapper = styled(Grid)`
  margin-bottom: 24px; 
`

const StandardsPage = () => {
  const { routeTo } = useRouting()
  const classes = useStyles();
  const { t } = useTranslation();
  const user = useSelector((state) => state.authReducer.user!)
  const properties = useSelector((state) => state.propertyReducer.properties)
  const count = useSelector((state) => state.propertyReducer.count)
  const {
    searchState,
    pageState
  } = useContext(QuestionnaireContext);
  const [filterBy, setFilterBy] = useState<string>('show_all')
  const userProfile = useSelector<IUser | undefined>(getUserProfile);
  const [page, setPage] = pageState;
  const [search, setSearch] = searchState;
  const [selectedCompanyId, setSelectedCompanyId] = useState('')
  const [loading, setLoading] = useState(false)

  const dispatch = useDispatch()
  useEffect(() => {
    setLoading(true)
    dispatch(async (dispatch: AppDispatchType) => {
      await fetchPagedProperties(user.token, page, search, {progress: filterBy, company: selectedCompanyId})(dispatch);
      scrollToTop();
      setLoading(false)
    })
    return () => {
      dispatch(clearProperties())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userProfile?.is_staff) {
      dispatch(fetchCompaniesInLive(user!.token, false))
    }
  }, [userProfile]);

  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const handleClick = () => {
    dispatch(clearProperty())

    routeTo(urls.createNewProperty)
  }
  const handlePropertyEdit = async (id: any) => {
    dispatch(clearA11yes())
    dispatch(async (dispatch: AppDispatchType) => {
      return propertyFetchProperty(id, user.token)
        .then((response: any) => {
          dispatch({
            type: types.FETCH_PROPERTY,
            property: response.data,
          });
          routeTo(urls.editProperty, { id })
        })
    });
  }
  const scrollToTop = () => {
    const header = document.getElementById("app-header");
    header!.scrollIntoView({
      behavior: "smooth"
    });
  }

  const handlePropertyClick = async (id: string) => {
    await dispatch(clearA11yes())
    dispatch(async (dispatch: AppDispatchType) => {
      return propertyFetchProperty(id, user.token)
        .then((response) => {
          dispatch({
            type: types.FETCH_PROPERTY,
            property: response.data,
          });
          routeTo(urls.classify, { id })
        })
    });
  }

  const handleFacilitiesEdit = async (id: string) => {
    dispatch(async (dispatch: AppDispatchType) => {
      return propertyFetchProperty(id, user.token)
        .then((response) => {
          dispatch({
            type: types.FETCH_PROPERTY,
            property: response.data,
          });
          routeTo(urls.selectFacilities, { id })
        })
    });
  }

  useEffect(() => {
    setLoading(true)
    dispatch(clearProperties())
    dispatch(async (dispatch: AppDispatchType) => {
      await fetchPagedProperties(user.token, 1, search, {progress: filterBy, company: selectedCompanyId})(dispatch);
      scrollToTop();
      setLoading(false)
    })
  }, [selectedCompanyId]);

  const handleChangePage = (_: any, value: number) => {
    setLoading(true)
    dispatch(async (dispatch: AppDispatchType) => {
      await fetchPagedProperties(user.token, value, search, {progress: filterBy, company: selectedCompanyId})(dispatch);
      setPage(value);
      scrollToTop();
      setLoading(false)
    })
  };

  const handleChangeFilter = (type: string) => {
    setLoading(true)
    dispatch(async (dispatch: AppDispatchType) => {
      await fetchPagedProperties(user.token, 1, search, {progress: type, company: selectedCompanyId})(dispatch);
      setFilterBy(type)
      setLoading(false)
    })
  };

  return (
    <Box p={12} className={`${classes.box} disable-scrolls`} overflow="scroll">
      <Helmet title={t("propertyListPage_propertyList")} />
      <MuiTypography variant="h3" gutterBottom display="inline" id="app-header">
        {t("propertyListPage_propertyList")}
      </MuiTypography>
      <Divider my={6} /> 
      <SearchWrapper container>
        <Grid item xs={12} md={3}>
          <PropertySearch
            selectedCompanyId={selectedCompanyId}
            filterBy={filterBy}
            search={search}
            onSetPage={setPage}
            onSetSearch={setSearch}
            onSetLoading={setLoading}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <PropertyFilters
            filterBy={filterBy}
            selectedCompanyId={selectedCompanyId}
            handleChangeFilter={handleChangeFilter}
            onSetSelectCompanyId={setSelectedCompanyId}
          />
        </Grid>
      </SearchWrapper>
      <Grid container spacing={6}>
        {page === 1 && <Grid item md={4} sm={6} xs={12} lg={3} style={{ display: "flex" }}>
          <CreateNewProperty handleClick={handleClick} />
        </Grid>}
        {!loading && properties && properties.length > 0 ? (
          <RenderProperties
            properties={properties}
            onClick={handlePropertyClick}
            onEdit={handlePropertyEdit}
            onEditFacilities={handleFacilitiesEdit}
          />
        ) : null}
        {!loading && properties.length === 0 ? (
          <Grid item md={8} sm={6} xs={12} lg={9} style={{height: 281}}/>
        ) : null}
        {loading ? (
          <GridSkeleton
            count={7}
            height={259}
            container={false}
          />
        ) : null}
      </Grid>
      <Box mt={6}>
        {(properties && count && (count + 1) > 8) ? (
          <Pagination
            size={sm ? "small" : "medium"}
            count={Math.ceil(count / 8)}
            page={page}
            onChange={handleChangePage}
            showFirstButton
            showLastButton
          />
        ) : null}
      </Box>
    </Box>
  );
}

export default StandardsPage;
